import { useState } from 'react'
import { PALETTE as P } from '../tokens'
import { useAuth } from '../context/AuthContext'
import { usersApi } from '../api/users'

/**
 * ConsentModal — one-time ask on first app entry after onboarding.
 *
 * Gated by ProtectedRoute on `user.research_consent === null` (NULL =
 * "not yet asked"). Either button PATCHes the backend and patches the
 * in-memory user object so the modal unmounts. If the PATCH fails we
 * still unmount and let the user through — access is never blocked by
 * this surface, per spec.
 */
export function ConsentModal() {
  const { token, patchUser } = useAuth()
  const [submitting, setSubmitting] = useState(false)

  async function respond(researchConsent) {
    if (submitting) return
    setSubmitting(true)

    // Optimistically patch local state so the modal unmounts before the
    // round-trip completes — avoids the "button froze for a second" feel.
    // If the API call fails, the next /auth/me refresh will repair the
    // value; in the worst case the user sees the modal once more.
    patchUser({ research_consent: researchConsent })

    try {
      await usersApi.setConsent(researchConsent, token)
    } catch {
      // Never block access on a consent save — spec. Leave the optimistic
      // patch in place; a later fetch will reconcile.
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(28,24,20,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
      animation: 'fadeIn 0.25s ease',
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes consentRise {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="consent-title"
        style={{
          background: P.bg,
          borderRadius: 22,
          padding: '32px 28px 26px',
          maxWidth: 420,
          width: '100%',
          boxShadow: '0 12px 44px rgba(28,24,20,0.22)',
          animation: 'consentRise 0.32s ease forwards',
        }}
      >
        <div style={{
          fontSize: 10, color: P.primary,
          letterSpacing: '0.18em', textTransform: 'uppercase',
          fontWeight: 500, marginBottom: 14, textAlign: 'center',
        }}>
          Research Consent
        </div>

        <h2
          id="consent-title"
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 26, fontStyle: 'italic', fontWeight: 400,
            color: P.text, lineHeight: 1.2, marginBottom: 16,
            textAlign: 'center',
          }}
        >
          You&rsquo;re Part of Something Bigger
        </h2>

        <p style={{
          fontSize: 13.5, color: P.textMid, fontWeight: 300,
          lineHeight: 1.7, marginBottom: 22, textAlign: 'center',
        }}>
          Haiven is learning how to help people feel more coherent across
          body, mind, and behavior. Your anonymized signal — never your
          name or identifying details — helps us make that real for more
          people. Your answer is private and you can change it anytime.
        </p>

        <button
          onClick={() => respond(true)}
          disabled={submitting}
          style={{
            width: '100%', padding: 15,
            background: P.dark, border: 'none', borderRadius: 14,
            cursor: submitting ? 'default' : 'pointer',
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 16, fontStyle: 'italic',
            color: P.bg,
            letterSpacing: '0.01em',
            opacity: submitting ? 0.5 : 1,
            transition: 'opacity 0.15s',
            marginBottom: 10,
          }}
        >
          Yes, I&rsquo;d like to contribute to research
        </button>

        <button
          onClick={() => respond(false)}
          disabled={submitting}
          style={{
            width: '100%', padding: 14,
            background: 'none',
            border: `1.5px solid ${P.border}`,
            borderRadius: 14,
            cursor: submitting ? 'default' : 'pointer',
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 13.5, fontWeight: 400,
            color: P.textMid,
            opacity: submitting ? 0.5 : 1,
            transition: 'all 0.15s',
          }}
        >
          No thanks, keep my data private
        </button>

        <p style={{
          fontSize: 11, color: P.textFaint, fontWeight: 300,
          lineHeight: 1.55, marginTop: 16, textAlign: 'center',
        }}>
          Declining keeps every part of Haiven available to you. It does turn
          off one thing: including your written entries in anything you share
          with someone else. Change it anytime in Settings.
        </p>
      </div>
    </div>
  )
}
